import { useCallback, useMemo } from 'react';
import { AlertType, Icon } from '../../../../components';
import { AppRoutes } from '../../../../constants';
import { useGetUserPermissions, useToast } from '../../../../hooks';
import { ProspectCategory, ProspectStatus, SimulationsFilterKeys } from '../../../../types';
import { conditionalArray } from '../../../../utils';
import { SimActionType } from '../../SimActionModals';

interface UseSimulationDetailsHeaderActionsProps {
  isDraft: boolean;
  isUsed: boolean;
  personaId: string;
  prospectCategory: ProspectCategory;
  prospectUserId: string;
  status: ProspectStatus;
  openSimModal: (type: SimActionType) => void;
}

/**
 * Secondary (header) action buttons for the simulation details.
 * Handles actions like archive, delete, and copy link.
 */
const useSimulationDetailsHeaderActions = ({
  isDraft,
  isUsed,
  personaId,
  prospectCategory,
  prospectUserId,
  status,
  openSimModal,
}: UseSimulationDetailsHeaderActionsProps) => {
  const { showToast } = useToast();

  const { getCanUserEditSimInSelfService } = useGetUserPermissions();
  const canUserEditSimInSelfService = getCanUserEditSimInSelfService(prospectCategory, isDraft, prospectUserId);

  const canArchiveOrDeleteSim = canUserEditSimInSelfService && status === ProspectStatus.ACTIVE;

  const handleCopy = useCallback(() => {
    let url = window.location.origin;

    // If the user can edit the simulation, copy the link to the prospect page.
    // Otherwise, copy the link to the simulation page with the prospect filter applied.
    if (canUserEditSimInSelfService) url += `${AppRoutes.PROSPECT}/${personaId}`;
    else url += `${AppRoutes.SIMULATIONS}?${SimulationsFilterKeys.PROSPECT}=${personaId}`;

    navigator.clipboard.writeText(url);
    showToast({ type: AlertType.SUCCESS, message: 'Link to simulation copied to clipboard' });
  }, [showToast, personaId, canUserEditSimInSelfService]);

  const actions = useMemo(
    () => [
      ...conditionalArray(canArchiveOrDeleteSim, {
        destructive: true,
        icon: isUsed ? Icon.ARCHIVE : Icon.TRASH,
        tooltip: isUsed ? 'Archive simulation' : 'Delete simulation',
        onClick: () => openSimModal(isUsed ? SimActionType.ARCHIVE : SimActionType.DELETE),
      }),
      {
        icon: Icon.LINK,
        tooltip: 'Copy link to simulation',
        onClick: handleCopy,
      },
    ],
    [canArchiveOrDeleteSim, isUsed, openSimModal, handleCopy]
  );

  return actions;
};

export default useSimulationDetailsHeaderActions;
